import { useUserStore } from '@geta/kolumbus-frontend/features/authentication';
import { useMainStore } from '@/features/common/stores';
import { useDeparturesSearchStore } from '@/features/departures/stores';
import { useTripSearchStore } from '@/features/trips/stores';
import { useContainer } from '@/plugins/inversify';
import { IBookingService, IBookingServiceId } from '@/services';
import { BookingDto, BookingMethod, BookingStatus, CreateBookingDto, StopOrderDto } from '@/types/webapi';
import { isAxiosError } from 'axios';
import { defineStore } from 'pinia';
import { computed, nextTick, ref, toValue } from 'vue';
import { UserInfo } from '../models';
import { isAfter, isBefore, isDate, addDays, isSameDay } from 'date-fns';
import { BookingParams, createBookingParams } from '../functions';
import { BookingError } from '@/features/common/models';

export const defaultUserInfo = (): UserInfo => ({
    phone: {
        countryCode: '+47',
        number: ''
    }
});

export const useBookingStore = defineStore('bookingStore', () => {
    const userStore = useUserStore();
    const mainStore = useMainStore();
    const tripSearchStore = useTripSearchStore();
    const departureSearchStore = useDeparturesSearchStore();

    const container = useContainer();
    const bookingService = container.get<IBookingService>(IBookingServiceId);

    const bookingIsEnabled = computed(() => mainStore.config.bookingConfig?.enabled);
    const maxBookingExceeded = ref(false);
    const isLoading = ref(false);
    const isBooking = ref(false);
    const bookings = ref<BookingDto[]>([]);
    const selectedBooking = ref<BookingDto>();
    const stopOrders = ref<StopOrderDto[]>([]);
    const createBookingProps = computed<BookingParams | undefined>(() => {
        if (!bookingIsEnabled.value) return undefined;

        if (departureSearchStore.selectedDeparture) {
            return createBookingParams(departureSearchStore.selectedDeparture);
        }

        if (tripSearchStore.selectedTripPattern) {
            // generate params from first leg where onlineBookingIsAvailable=true
            return createBookingParams(tripSearchStore.selectedTripPattern.legs?.find(x => x.bookingMetadata?.onlineBookingIsAvailable));
        }

        return undefined;
    });
    const onlineBookingIsAvailable = computed(() => {
        if (!createBookingProps.value) return false;

        return createBookingProps.value.bookingMethods.includes(BookingMethod.Online);
    });
    const bookingDeadlineExceeded = computed(
        () =>
            isDate(createBookingProps.value?.bookingLimitDateTime) &&
            isAfter(mainStore.currentTime, createBookingProps.value.bookingLimitDateTime)
    );
    const bookingTooFarAhead = computed(
        () =>
            isDate(createBookingProps.value?.aimedDepartureDateTime) &&
            isAfter(createBookingProps.value.aimedDepartureDateTime, addDays(mainStore.currentTime, 7))
    );
    const canBook = computed(
        () => !!createBookingProps.value && !bookingDeadlineExceeded.value && !selectedBooking.value && !bookingTooFarAhead.value
    );
    const canCancel = computed(
        () =>
            selectedBooking.value?.isCancellationAllowed &&
            selectedBooking.value.status !== BookingStatus.Cancelled &&
            (!isDate(selectedBooking.value?.bookingCancellationLimitDateTime) ||
                isBefore(mainStore.currentTime, selectedBooking.value.bookingCancellationLimitDateTime))
    );
    const userInfo = ref<UserInfo>(defaultUserInfo());
    const userInfoIsValid = computed(() => userStore.isLoggedIn || !!(userInfo.value.phone?.countryCode && userInfo.value.phone.number));

    let loadStopOrderAbortController: AbortController | undefined;
    let loadBookingsAbortController: AbortController | undefined;

    async function loadStopOrder() {
        try {
            isLoading.value = false;

            // abort previous request
            loadStopOrderAbortController?.abort();
            loadStopOrderAbortController = new AbortController();

            const { serviceJourneyRef, activeDate, stopSequence } = createBookingProps.value || {};

            // check if params are valid
            if (!serviceJourneyRef || !activeDate || !stopSequence) return;

            // load stop order, and add as booking to bookings list
            const stopOrder = await bookingService.getStopOrder(
                { serviceJourneyRef, activeDate, stopSequence },
                {
                    signal: loadStopOrderAbortController.signal
                }
            );

            // if no stop order, do nothing
            if (!stopOrder) return;

            // add or update stop order
            const index = stopOrders.value.findIndex(x => x.id === stopOrder.id);

            if (index !== -1) {
                stopOrders.value[index] = stopOrder;
            } else {
                stopOrders.value.push(stopOrder);
            }
        } catch (e) {
            if (isAxiosError(e)) {
                // in case of AbortController being triggered, do nothing
                if (e.name === 'CanceledError') return;
            }

            mainStore.registerError(e);
        } finally {
            isLoading.value = false;
        }
    }

    async function loadBookings() {
        selectedBooking.value = undefined;

        // abort previous request
        loadBookingsAbortController?.abort();
        loadBookingsAbortController = new AbortController();

        if (!bookingIsEnabled.value || !userStore.isLoggedIn) return;

        try {
            isLoading.value = true;

            // get all user bookings
            bookings.value = await bookingService.getAllBookings({ signal: loadBookingsAbortController.signal });
            maxBookingExceeded.value = bookings.value.length >= 2;

            await nextTick();

            // set matching booking as selectedBooking
            selectedBooking.value = bookings.value.find(
                x =>
                    !!createBookingProps.value &&
                    isSameDay(x.activeDate, createBookingProps.value.activeDate) &&
                    x.serviceJourneyRef === createBookingProps.value.serviceJourneyRef &&
                    x.stopSequence === createBookingProps.value.stopSequence
            );

            isLoading.value = false;
        } catch (e) {
            if (isAxiosError(e)) {
                // in case of AbortController being triggered, do nothing
                if (e.name === 'CanceledError') return;

                if (e.response?.status !== 401) {
                    mainStore.registerError(new BookingError(e.response?.data || ''));
                }
            } else {
                console.error(e);
            }

            isLoading.value = false;
        }
    }

    async function loadBooking(id: string) {
        if (!id) return;

        try {
            isLoading.value = true;

            selectedBooking.value = await bookingService.getBooking(id);
        } catch (e) {
            if (isAxiosError(e) && e.response?.status !== 404) {
                mainStore.registerError(new BookingError(e.response?.data || ''));
            } else {
                console.error(e);
            }
        } finally {
            isLoading.value = false;
        }
    }

    async function createBooking(recaptchaToken?: string, phoneVerificationCode?: string) {
        if (isBooking.value) return;

        isBooking.value = true;

        const _createBookingProps = toValue(createBookingProps);
        if (!_createBookingProps || !userInfoIsValid.value) return;

        try {
            isLoading.value = true;

            const { countryCode, number } = userInfo.value.phone || {};
            const params: CreateBookingDto = {
                ..._createBookingProps,
                name: userInfo.value.name,
                phoneCountryCode: countryCode,
                phoneNumber: number,
                recaptchaToken,
                phoneVerificationCode
            };

            selectedBooking.value = await bookingService.createBooking(params);
        } catch (e) {
            if (isAxiosError(e)) {
                // if already exists, load it up
                if (e.response?.status === 302 && e.response.data) {
                    await loadBooking(e.response.data);
                    return;
                }

                // if precondition failed, just return
                if (e.response?.status === 412) {
                    if (e.response.data === 'BOOKING_LIMIT_REACHED') {
                        maxBookingExceeded.value = true;
                        return;
                    }

                    if (e.response.data === 'REQUEST_VALIDATION_FAILED') {
                        // TODO: handle this better
                        throw new BookingError('REQUEST_VALIDATION_FAILED');
                    }
                }

                mainStore.registerError(new BookingError(e.response?.data || ''));
            } else {
                console.error(e);
            }
        } finally {
            isLoading.value = false;
            isBooking.value = false;
        }
    }

    async function cancelBooking() {
        if (!selectedBooking.value) return;

        try {
            isLoading.value = true;

            selectedBooking.value = await bookingService.cancelBooking(selectedBooking.value.id);
            // Remove the cancelled booking from bookings collection
            bookings.value = bookings.value.filter(booking => booking.id !== selectedBooking.value?.id);
        } catch (e) {
            if (isAxiosError(e)) {
                mainStore.registerError(new BookingError(e.response?.data || ''));
            } else {
                console.error(e);
            }
        } finally {
            isLoading.value = false;
        }
    }

    return {
        bookingIsEnabled,
        onlineBookingIsAvailable,
        bookings,
        selectedBooking,
        maxBookingExceeded,
        createBookingProps,
        bookingDeadlineExceeded,
        canBook,
        canCancel,
        stopOrders,
        isLoading,
        isBooking,
        userInfo,
        userInfoIsValid,
        bookingTooFarAhead,
        loadBookings,
        loadBooking,
        createBooking,
        cancelBooking,
        loadStopOrder
    };
});
